import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Sparkles, Home, MapPinOff } from 'lucide-react';

const Accommodation = () => {
  const accommodations = [
    {
      name: 'Pavilion',
      guests: [
        { name: 'Robyn Maddison', role: 'Bride' },
        { name: 'Angelo Minchio', role: 'Groom' },
        { name: 'Nio Maddison', role: 'Ring Bearer' },
        { name: 'Deanna Minchio' },
        { name: 'Warren Johnson' },
        { name: 'Jake Minchio' },
        { name: 'Sarah Minchio' },
        { name: 'Miles Minchio' },
        { name: 'Matthew Minchio' },
        { name: 'Mikayla Williams' },
        { name: 'Ares Williams' },
        { name: 'Jesse Minchio' },
        { name: 'Dion Assenbruck' },
        { name: 'Adam Ryan' },
        { name: 'Kerry Ryan' },
        { name: 'Aaliyah Ryan', role: 'Flower Girl' },
        { name: 'Leo Ryan' }
      ]
    },
    {
      name: 'Blue Gum Cottage',
      guests: [
        { name: 'Ian Maddison' },
        { name: 'Louise Pivotto' },
        { name: 'Adrian Pivotto' },
        { name: 'Alyssa Parker' }
      ]
    },
    {
      name: 'Ironbark Cottage',
      guests: [
        { name: 'Karen Maddison' },
        { name: 'Bruce Maddison' },
        { name: 'Isabella Maddison' }
      ]
    },
    {
      name: 'Grey Gum Cottage',
      guests: [
        { name: 'Shannay Pivotto' },
        { name: 'Ryan McAndrew' },
        { name: 'Rebecca McWilliam', role: 'Maid of Honour' },
        { name: 'Hayden Kaer' }
      ]
    },
    {
      name: 'Spotted Gum Cottage',
      guests: [
        { name: 'Peter Assenbruck' },
        { name: 'Camerita Minchio' },
        { name: 'Milena Minchio' }
      ]
    }
  ];

  const offVenue = [
    { name: 'Anthony Jong' },
    { name: 'Sally Robertson' },
    { name: 'Sian Strogusz' },
    { name: 'Matt Strogusz' },
    { name: 'Ari Strogusz' },
    { name: 'Katelyn Bell' },
    { name: 'Craig Bell' },
    { name: 'Claire Bell', role: 'Flower Girl' },
    { name: 'Kelly Laxton' },
    { name: 'Tim Laxton' },
    { name: 'Mila Laxton' },
    { name: 'Marlie Laxton' }
  ];

  return (
    <section id="accommodation" className="py-20 bg-background" data-testid="accommodation-section">
      <div className="section-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 space-y-4"
        >
          <div className="inline-flex items-center gap-2 px-6 py-2 bg-primary/10 backdrop-blur-sm rounded-full border border-primary/20">
            <Sparkles className="w-4 h-4" style={{ color: '#E6C76B' }} />
            <span className="text-sm text-primary font-semibold tracking-wider uppercase">Where You'll Stay</span>
          </div>
          <h2 className="font-serif text-5xl sm:text-6xl font-normal text-primary">Accommodation</h2>
          <div 
            className="h-1.5 w-32 mx-auto rounded-full"
            style={{ 
              backgroundImage: 'linear-gradient(135deg, #F9E27D 0%, #D4AF37 40%, #B8962E 60%, #F6E27A 100%)',
              boxShadow: '0 2px 8px rgba(212, 175, 55, 0.3)'
            }}
          />
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            See where everyone is staying at Sherwood Farm
          </p>
        </motion.div>

        {/* Site Plan */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-16"
        >
          <Card className="border-2 border-primary/20 shadow-lg overflow-hidden">
            <CardHeader className="bg-primary/5">
              <CardTitle className="text-2xl font-serif text-center text-primary">Sherwood Farm Site Plan</CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-8">
              <div className="flex justify-center">
                <img
                  src="https://customer-assets.emergentagent.com/job_our-day-1/artifacts/3nw1060s_image.png"
                  alt="Sherwood Farm Site Plan"
                  className="w-full max-w-5xl h-auto rounded-lg shadow-md cursor-pointer hover:shadow-xl transition-shadow duration-300"
                  onClick={(e) => window.open(e.target.src, '_blank')}
                  title="Click to view full size"
                />
              </div>
              <p className="text-center text-sm text-muted-foreground mt-4 italic">
                Click image to view full size
              </p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Accommodation Allocations */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h3 className="text-3xl font-serif text-center text-primary mb-8">Guest Allocations</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {accommodations.map((accommodation, index) => (
              <motion.div
                key={accommodation.name}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Card className={`h-full border-2 hover:shadow-xl transition-all duration-300 ${
                  accommodation.name === 'Pavilion' ? 'border-primary bg-primary/5' : 'border-primary/20'
                }`}>
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-2">
                      <Home className="w-5 h-5 text-primary" />
                      <Badge 
                        variant="outline" 
                        className="bg-primary/10 text-primary border-primary/30"
                      >
                        {accommodation.guests.length} guests
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-serif text-primary">
                      {accommodation.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {accommodation.guests.map((guest, idx) => (
                        <li 
                          key={idx} 
                          className="flex items-start gap-2 text-base"
                          data-testid={`accommodation-guest-${accommodation.name.replace(/\s+/g, '-').toLowerCase()}-${idx}`}
                        >
                          <span className="text-primary mt-1.5 font-bold text-lg">•</span>
                          <span className="text-foreground leading-relaxed flex items-center gap-2 flex-wrap">
                            {guest.name}
                            {guest.role && (
                              <Badge className="bg-primary/10 text-primary border-primary/30 text-xs font-normal" variant="outline">
                                {guest.role}
                              </Badge>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Off-Venue Guests */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="mt-10 max-w-3xl mx-auto"
          data-testid="accommodation-off-venue-card"
        >
          <Card className="border-2 border-primary/20">
            <CardHeader>
              <div className="flex items-center gap-3 mb-1">
                <MapPinOff className="w-5 h-5 text-primary" />
                <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">
                  {offVenue.length} guests
                </Badge>
              </div>
              <CardTitle className="text-xl font-serif text-primary">Staying Off Venue</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {offVenue.map((guest, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-2 text-base"
                    data-testid={`accommodation-guest-off-venue-${idx}`}
                  >
                    <span className="text-primary mt-1.5 font-bold text-lg">•</span>
                    <span className="text-foreground leading-relaxed flex items-center gap-2 flex-wrap">
                      {guest.name}
                      {guest.role && (
                        <Badge className="bg-primary/10 text-primary border-primary/30 text-xs font-normal" variant="outline">
                          {guest.role}
                        </Badge>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};

export default Accommodation;
